import React, { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, Clock, ChevronRight, Bookmark } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BuingGuideSkeleton from "./BuingGuideSkeleton";

const AllGuide = () => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/buying-guide/get`
        );
        setGuides(response.data.guides);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch guides. Please try again later.");
        setLoading(false);
      }
    };

    fetchGuides();
  }, []);

  // Format date to readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleNavigate = (guide) => {
    navigate(`/guide-details/${guide}`);
  };

  if (loading) {
    return <BuingGuideSkeleton />;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="bg-red-100 p-6 rounded-lg max-w-md text-center">
          <p className="text-red-700">{error}</p>
          <button
            className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition duration-300"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Buying Guides
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {guides.map((guide) => (
            <div
              key={guide._id}
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col"
            >
              {/* Card Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={guide.titleImageUrl}
                  alt={guide.headerTitle}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>

              {/* Card Content */}
              <div className="p-6 flex-grow">
                <div className="flex items-center mb-4 text-xs text-gray-500">
                  <Calendar size={14} className="mr-1" />
                  <span className="mr-4">{formatDate(guide.createdAt)}</span>
                  <Clock size={14} className="mr-1" />
                  <span>5 min read</span>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                  {guide.headerTitle}
                </h2>
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {guide.headerDescription}
                </p>

                {/* Materials Preview */}
                <div className="mt-4 space-y-3">
                  {guide.content.slice(0, 2).map((item) => (
                    <div key={item._id} className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-orange-500 mr-2"></div>
                      <span className="text-sm font-medium text-gray-700">
                        {item.title}
                      </span>
                    </div>
                  ))}
                  {guide.content.length > 2 && (
                    <div className="text-sm text-gray-500">
                      + {guide.content.length - 2} more materials
                    </div>
                  )}
                </div>
              </div>

              {/* Card Footer */}
              <div className="px-6 py-4 border-t border-gray-100">
                <button
                  className="inline-flex items-center text-orange-600 hover:text-orange-800 font-medium transition duration-300"
                  onClick={() => handleNavigate(guide._id)}
                >
                  Read Full Guide
                  <ChevronRight size={16} className="ml-1" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AllGuide;
